import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TimeTrackingProvider } from './contexts/TimeTrackingContext';
import LoginPage from './components/LoginPage';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import Jobs from './components/Jobs';
import PriorityProjects from './components/PriorityProjects';
import CompletedProjects from './components/CompletedProjects';
import RepeatOrders from './components/RepeatOrders';
import PointingSystem from './components/PointingSystem';
import TimeSheet from './components/TimeSheet';
import AdminPanel from './components/AdminPanel';
import Shifts from './components/Shifts';
import Capa from './components/Capa';
import Leaves from './components/Leaves';
import Cafeteria from './components/Cafeteria';
import CafeteriaConfirm from './components/CafeteriaConfirm';
import ClockIn from './components/ClockIn';
import PendingJobs from './components/PendingJobs';
import Reports from './components/Reports';
import Docs from './components/Docs';
import OnApprovals from './components/OnApprovals';
import Notifications from './components/Notifications';
import NotificationAlert from './components/NotificationAlert';
import ImportantMessages from './components/ImportantMessages';
import AuditTrails from './components/AuditTrails';
import CustomerPortal from './components/CustomerPortal';
import TVDisplay from './components/TVDisplay';

function AppContent() {
  const { isAuthenticated, loading, user, features } = useAuth();
  const [currentPage, setCurrentPage] = useState('jobs');
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [locationHash, setLocationHash] = useState(window.location.hash);

  useEffect(() => {
    const onHashChange = () => setLocationHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Guard: Only allow admin users to access the admin panel.
  // If a non-admin somehow navigates to the admin page, redirect them to the dashboard.
  useEffect(() => {
    // Back-office roles don't see the Dashboard. Clock-in roles land on the
    // Clock In page; dispatch (no clock-in) lands on Cafeteria. Keep them off
    // any page that isn't in their allowed set.
    const clockInRoles = ['receptionist', 'accounts', 'support', 'it'];
    if (user && clockInRoles.includes(user.role)) {
      const allowed = ['clock-in', 'timesheet', 'cafeteria'];
      if (!allowed.includes(currentPage)) { setCurrentPage('clock-in'); return; }
    }
    if (user && user.role === 'dispatch') {
      if (currentPage !== 'cafeteria') { setCurrentPage('cafeteria'); return; }
    }
    if (currentPage === 'admin' && (!user || user.role !== 'admin')) {
      setCurrentPage('dashboard');
    }
    if (currentPage === 'docs' && (!user || user.role !== 'admin')) {
      setCurrentPage('dashboard');
    }
    if (currentPage === 'audit-trails' && (!user || !['admin', 'pm'].includes(user.role))) {
      setCurrentPage('dashboard');
    }
    // Priority Projects is now available to all staff (normal users see skill-matched jobs).
    if (currentPage === 'priority-projects' && !user) {
      setCurrentPage('dashboard');
    }
    if (currentPage === 'pending-jobs' && (!user || !['admin', 'pm', 'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'].includes(user.role))) {
      setCurrentPage('dashboard');
    }
    if (currentPage === 'shifts' && (!user || !['admin', 'pm', 'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'].includes(user.role))) {
      setCurrentPage('dashboard');
    }
    // Repeat Orders is open to all users; access is controlled per-user by the
    // repeat_orders feature flag rather than by role.
    if (currentPage === 'repeat-orders' && (!user || features?.repeat_orders === false)) {
      setCurrentPage('dashboard');
    }
    // Pointing System is open to all production staff: managers see the full
    // scoreboard, normal operators see only their own points.
    if (currentPage === 'pointing-system' && !user) {
      setCurrentPage('dashboard');
    }
  }, [currentPage, user, features]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Allow customers to access the portal without staff authentication
  // if (!isAuthenticated) {
  //   if (locationHash.startsWith('#customer-portal') || locationHash.startsWith('#customer-reset')) {
  //     return <CustomerPortal />;
  //   }
  //   // If a customer session exists, redirect to the customer portal
  //   if (localStorage.getItem('cp_token')) {
  //     window.location.hash = '#customer-portal';
  //     return <CustomerPortal />;
  //   }
  //   return <LoginPage />;
  // }

  // Public customer portal
  if (window.location.hostname === "customers.veepeegraphics.com") {
    return <CustomerPortal />;
  }

  // Staff login
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Cafeteria staff get a dedicated full-screen kiosk — no app navigation.
  if (user?.role === 'cafeteria') {
    return <CafeteriaConfirm />;
  }

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'jobs':
        return <Jobs />;
      case 'priority-projects':
        return <PriorityProjects />;
      case 'completed-projects':
        return <CompletedProjects />;
      case 'repeat-orders':
        return <RepeatOrders />;
      case 'timesheet':
        return <TimeSheet />;
      case 'admin':
        return <AdminPanel />;
      case 'shifts':
        return <Shifts />;
      case 'capa':
        return <Capa />;
      case 'leaves':
        return <Leaves />;
      case 'cafeteria':
        return <Cafeteria />;
      case 'clock-in':
        return <ClockIn />;
      case 'pending-jobs':
        return <PendingJobs />;
      case 'reports':
        return <Reports />;
      case 'pointing-system':
        return <PointingSystem />;
      case 'on-approvals':
        return <OnApprovals />;
      case 'notifications':
        return <Notifications />;
      case 'docs':
        return <Docs />;
      case 'important-messages':
        return <ImportantMessages />;
      case 'audit-trails':
        return <AuditTrails />;
      case 'customer-portal':
        return <CustomerPortal />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <TimeTrackingProvider>
      <div className="flex min-h-screen bg-gray-50">
        <Navigation
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          unreadNotifCount={unreadNotifCount}
        />
        <main className="flex-1 ml-72 transition-all duration-300">
          {renderCurrentPage()}
        </main>
      </div>
      {/* Blocking full-page alert overlay – rendered outside the scrollable main area */}
      <NotificationAlert onUnreadCountChange={setUnreadNotifCount} />
    </TimeTrackingProvider>
  );
}

function App() {
  // TV Display is a public page — no auth required.
  // Access it by navigating to the app URL with #tv hash (e.g. http://server/#tv)
  if (window.location.hash.startsWith('#tv')) {
    return <TVDisplay />;
  }

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
